/** Stabil länk-id + UTM/nl-parametrar för nyhetsbrev (ingen mottagar-PII i URL). */

export const NEWSLETTER_TRACKING_PARAM_PREFIXES = ['utm_', 'nl_'] as const;

/** Attribution-fönster: senaste nyhetsbrevs-touch inom 30 dagar. */
export const NEWSLETTER_ATTRIBUTION_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

export type NewsletterStampedLink = {
  linkKey: string;
  href: string;
  hrefNormalized: string;
  label: string | null;
};

export type StampNewsletterLinksResult = {
  html: string;
  links: NewsletterStampedLink[];
};

const TRACKING_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
  'nl_c',
  'nl_l',
  'nl_t',
]);

/** Ta bort spårningsparametrar för stabil länknyckel; behåll övriga query-parametrar. */
export function normalizeHrefForLinkKey(rawUrl: string): string {
  try {
    const u = new URL(rawUrl);
    for (const key of [...u.searchParams.keys()]) {
      if (TRACKING_KEYS.has(key) || key.startsWith('utm_') || key.startsWith('nl_')) {
        u.searchParams.delete(key);
      }
    }
    u.hash = '';
    // Stabil host casing
    u.hostname = u.hostname.toLowerCase();
    return u.toString();
  } catch {
    return String(rawUrl || '')
      .trim()
      .split('#')[0]
      .replace(/([?&])(utm_|nl_)[^&]*/gi, '')
      .replace(/[?&]$/, '');
  }
}

/** Kort stabil nyckel från normaliserad URL (ej kryptografisk). */
export function linkKeyFromNormalizedHref(hrefNormalized: string): string {
  let h = 2166136261;
  const s = String(hrefNormalized || '');
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return `l${(h >>> 0).toString(36)}`;
}

export function isUnsubscribeHref(url: string, unsubscribeUrlPart = '/avregistrera'): boolean {
  const lower = url.toLowerCase();
  return lower.includes(unsubscribeUrlPart.toLowerCase()) || lower.includes('unsubscribe');
}

/**
 * Stämpla innehållslänkar med UTM + nl_c/nl_l. Avregistrering orörd.
 * Bevarar befintliga URL-parametrar; sätter utm_* / nl_* endast om saknas (utm_content skrivs om till link_key).
 */
export function stampNewsletterLinks(
  html: string,
  opts: {
    campaignId: string;
    campaignSlug?: string | null;
    unsubscribeUrlPart?: string;
  },
): StampNewsletterLinksResult {
  const unsubPart = opts.unsubscribeUrlPart ?? '/avregistrera';
  const campaignParam = String(opts.campaignSlug || opts.campaignId || 'newsletter').slice(0, 80);
  const campaignId = String(opts.campaignId || '').slice(0, 80);
  const byKey = new Map<string, NewsletterStampedLink>();

  const stamped = String(html ?? '').replace(
    /<a\b([^>]*?)href=(["'])(https?:\/\/[^"']+)\2([^>]*)>([\s\S]*?)<\/a>/gi,
    (
      _full,
      pre: string,
      quote: string,
      url: string,
      post: string,
      inner: string,
    ) => {
      if (isUnsubscribeHref(url, unsubPart)) {
        return `<a${pre}href=${quote}${url}${quote}${post}>${inner}</a>`;
      }

      const hrefNormalized = normalizeHrefForLinkKey(url);
      const linkKey = linkKeyFromNormalizedHref(hrefNormalized);
      const label = String(inner || '')
        .replace(/<[^>]+>/g, '')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 200) || null;

      if (!byKey.has(linkKey)) {
        byKey.set(linkKey, {
          linkKey,
          href: hrefNormalized,
          hrefNormalized,
          label,
        });
      } else if (!byKey.get(linkKey)!.label && label) {
        byKey.get(linkKey)!.label = label;
      }

      try {
        const u = new URL(url);
        if (!u.searchParams.has('utm_source')) u.searchParams.set('utm_source', 'newsletter');
        if (!u.searchParams.has('utm_medium')) u.searchParams.set('utm_medium', 'email');
        if (!u.searchParams.has('utm_campaign')) u.searchParams.set('utm_campaign', campaignParam);
        u.searchParams.set('utm_content', linkKey);
        u.searchParams.set('nl_c', campaignId);
        u.searchParams.set('nl_l', linkKey);
        return `<a${pre}href=${quote}${u.toString()}${quote}${post}>${inner}</a>`;
      } catch {
        const sep = url.includes('?') ? '&' : '?';
        const q =
          `utm_source=newsletter&utm_medium=email&utm_campaign=${encodeURIComponent(campaignParam)}` +
          `&utm_content=${encodeURIComponent(linkKey)}&nl_c=${encodeURIComponent(campaignId)}&nl_l=${encodeURIComponent(linkKey)}`;
        return `<a${pre}href=${quote}${url}${sep}${q}${quote}${post}>${inner}</a>`;
      }
    },
  );

  return { html: stamped, links: [...byKey.values()] };
}

/** Heuristik för misstänkt automatisk öppning/klick (UA). Beskeden är osäkra. */
export function suspectAutomatedUserAgent(userAgent: string | null | undefined): boolean {
  const ua = String(userAgent || '').toLowerCase();
  if (!ua) return false;
  const needles = [
    'bot',
    'crawler',
    'spider',
    'preview',
    'urlscan',
    'proofpoint',
    'barracuda',
    'mimecast',
    'microsoft office',
    'outlook-ios',
    'yahoo mail proxy',
    'googleimageproxy',
    'bingpreview',
    'slackbot',
    'twitterbot',
    'facebookexternalhit',
    'headless',
    'curl/',
    'wget/',
    'python-requests',
  ];
  return needles.some((n) => ua.includes(n));
}
